import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Copy,
  Download,
  FileText,
  History,
  Loader2,
  Plus,
  Save,
  Send,
  ShieldCheck,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { formatINR, cn } from "@/lib/utils";
import { INDIAN_STATES } from "@/lib/indianStates";
import { QuoteStatusBadge, QUOTE_STATUS_LABELS } from "./QuoteStatusBadge";
import { SendQuoteDialog } from "./SendQuoteDialog";
import type { LastQuotedPrice, ProductVariantRecord, QuotationLineRecord, QuotationRecord } from "@/types";

const GST_RATES = [0, 5, 12, 18, 28];
const CUSTOM = "__custom__";

const DELIVERY_TERMS: Array<[string, string]> = [
  ["EX_MILL", "Ex-mill / Ex-works"],
  ["FOR", "FOR destination"],
  ["FOB", "FOB"],
  ["CIF", "CIF"],
  ["DOOR", "Door delivery"],
];

interface EditableLine {
  key: string;
  product_id: string; // "" = not chosen, CUSTOM = custom line
  product_name: string;
  product_code?: string | null;
  description: string;
  hsn_sac_code: string;
  unit: string;
  quantity: string;
  unit_price: string;
  discount_pct: string;
  gst_rate: number;
  variant_id: string;
  variant_name: string;
  /** Specification as quoted (shown read-only); rebuilt by the server on save */
  specs: string;
  tolerance_pct: string;
  packing: string;
  delivery_date: string;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** Mirrors the backend calculation so totals update while typing. */
function computeLine(l: EditableLine) {
  const gross = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
  const discount = round2((gross * (Number(l.discount_pct) || 0)) / 100);
  const taxable = round2(gross - discount);
  const tax = round2((taxable * l.gst_rate) / 100);
  return { gross, discount, taxable, tax, total: round2(taxable + tax) };
}

const toEditable = (l: QuotationLineRecord, idx: number): EditableLine => ({
  key: l.id || `line-${idx}-${Date.now()}`,
  product_id: l.product_id || CUSTOM,
  product_name: l.product_name,
  product_code: l.product_code,
  description: l.description || "",
  hsn_sac_code: l.hsn_sac_code || "",
  unit: l.unit || "NOS",
  quantity: String(Number(l.quantity)),
  unit_price: String(Number(l.unit_price)),
  discount_pct: String(Number(l.discount_pct)),
  gst_rate: Number(l.gst_rate),
  variant_id: l.variant_id || "",
  variant_name: l.variant_name || "",
  specs: (l.spec_snapshot?.specs || []).map((sp) => `${sp.label}: ${sp.value}`).join(" | "),
  tolerance_pct: l.tolerance_pct !== null && l.tolerance_pct !== undefined ? String(Number(l.tolerance_pct)) : "",
  packing: l.packing || "",
  delivery_date: l.delivery_date ? String(l.delivery_date).slice(0, 10) : "",
});

const formatDate = (d?: string | null) =>
  d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const formatDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const HISTORY_LABELS: Record<string, string> = {
  CREATED: "Created",
  SUBMITTED: "Submitted for approval",
  AUTO_APPROVED: "Auto-approved",
  APPROVED: "Approved",
  APPROVAL_REJECTED: "Sent back to draft",
  SENT: "Sent to customer",
  REVISED: "Revised",
  REVISION_DISCARDED: "Revision discarded",
  ACCEPTED: "Accepted by customer",
  REJECTED: "Rejected by customer",
};

export function QuoteEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission, user } = useAuth();

  const [quote, setQuote] = useState<QuotationRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [attentionLabel, setAttentionLabel] = useState("");

  const [header, setHeader] = useState({
    contact_id: "",
    quote_date: "",
    valid_until: "",
    gst_type: "INTRA" as "INTRA" | "INTER",
    place_of_supply: "",
    terms: "",
    notes: "",
    customer_notes: "",
    delivery_terms: "",
    payment_terms: "",
  });
  const [variantsByProduct, setVariantsByProduct] = useState<Record<string, ProductVariantRecord[]>>({});
  const [lastPrices, setLastPrices] = useState<Record<string, LastQuotedPrice | null>>({});
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [dirty, setDirty] = useState(false);

  // Dialogs
  const [sendOpen, setSendOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveComment, setApproveComment] = useState("");
  const [reasonDialog, setReasonDialog] = useState<"REJECT_APPROVAL" | "CUSTOMER_REJECT" | null>(null);
  const [reason, setReason] = useState("");
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isDraft = quote?.quote_status === "DRAFT";
  const canEdit = isDraft && hasPermission("quote.update");
  const myId = String(user?.Id || "");
  const isMine = !!quote && (quote.owner_id === myId || quote.submitted_by_id === myId);

  const loadQuote = async () => {
    if (!id) return;
    try {
      const res = await api.get(`/api/Quotation/ById/${id}`);
      const q = unwrapObject<QuotationRecord>(res);
      setQuote(q);
      setHeader({
        contact_id: q.contact_id || "",
        quote_date: q.quote_date?.slice(0, 10) || "",
        valid_until: q.valid_until?.slice(0, 10) || "",
        gst_type: q.gst_type,
        place_of_supply: q.place_of_supply || "",
        terms: q.terms || "",
        notes: q.notes || "",
        customer_notes: q.customer_notes || "",
        delivery_terms: q.delivery_terms || "",
        payment_terms: q.payment_terms || "",
      });
      setLines((q.lines || []).map(toEditable));
      setDirty(false);
      return q;
    } catch (err: any) {
      error("Failed to load quote", err.message || "Quote not found");
      navigate("/quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadQuote();
  }, [id]);

  useEffect(() => {
    if (!quote?.contact) return;
    setAttentionLabel(`${quote.contact.first_name || ""} ${quote.contact.last_name || ""}`.trim());
  }, [quote?.id]);

  // ---------- totals (live while editing, server values otherwise) ----------
  const totals = useMemo(() => {
    if (!canEdit && quote) {
      return {
        subtotal: Number(quote.subtotal),
        discount: Number(quote.discount_total),
        taxable: Number(quote.taxable_total),
        cgst: Number(quote.cgst_total),
        sgst: Number(quote.sgst_total),
        igst: Number(quote.igst_total),
        roundOff: Number(quote.round_off),
        grand: Number(quote.grand_total),
        maxDiscount: Number(quote.max_discount_pct),
      };
    }
    let subtotal = 0, discount = 0, taxable = 0, tax = 0, maxDiscount = 0;
    for (const l of lines) {
      const c = computeLine(l);
      subtotal += c.gross;
      discount += c.discount;
      taxable += c.taxable;
      tax += c.tax;
      maxDiscount = Math.max(maxDiscount, Number(l.discount_pct) || 0);
    }
    tax = round2(tax);
    const cgst = header.gst_type === "INTRA" ? round2(tax / 2) : 0;
    const exact = round2(taxable + tax);
    const grand = Math.round(exact);
    return {
      subtotal: round2(subtotal),
      discount: round2(discount),
      taxable: round2(taxable),
      cgst,
      sgst: header.gst_type === "INTRA" ? round2(tax - cgst) : 0,
      igst: header.gst_type === "INTER" ? tax : 0,
      roundOff: round2(grand - exact),
      grand,
      maxDiscount,
    };
  }, [lines, header.gst_type, canEdit, quote]);

  const myLimit = typeof user?.discount_limit_pct === "number" ? user.discount_limit_pct : null;
  const needsApproval = myLimit !== null && totals.maxDiscount > myLimit;

  // ---------- line editing ----------
  const updateLine = (key: string, patch: Partial<EditableLine>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    setDirty(true);
  };

  const loadVariants = (productId: string) => {
    if (!productId || productId === CUSTOM || variantsByProduct[productId] || !hasPermission("product.read")) return;
    api
      .get(`/api/Product/Variants/${productId}`)
      .then((res) => {
        const rows = unwrapArray<ProductVariantRecord>(res).filter((v) => v.status !== false);
        setVariantsByProduct((prev) => ({ ...prev, [productId]: rows }));
      })
      .catch(() => setVariantsByProduct((prev) => ({ ...prev, [productId]: [] })));
  };

  const fetchLastPrice = (key: string, productId: string, variantId?: string) => {
    if (!quote || !productId || productId === CUSTOM) {
      setLastPrices((prev) => ({ ...prev, [key]: null }));
      return;
    }
    const qs = new URLSearchParams({ account_id: quote.account_id, product_id: productId });
    if (variantId) qs.set("variant_id", variantId);
    api
      .get(`/api/Quotation/LastPrice?${qs}`)
      .then((res) => setLastPrices((prev) => ({ ...prev, [key]: unwrapObject<LastQuotedPrice | null>(res, null) || null })))
      .catch(() => setLastPrices((prev) => ({ ...prev, [key]: null })));
  };

  // Shades of the products already on an editable quote
  useEffect(() => {
    if (!canEdit) return;
    for (const l of lines) {
      loadVariants(l.product_id);
      if (l.product_id && l.product_id !== CUSTOM && !(l.key in lastPrices)) fetchLastPrice(l.key, l.product_id, l.variant_id || undefined);
    }
  }, [canEdit, quote?.id, lines.map((l) => l.product_id).join(",")]);

  const pickProduct = (key: string, productId: string, row?: any) => {
    if (!row || productId === CUSTOM) {
      updateLine(key, { product_id: productId, product_code: null, product_name: productId === CUSTOM ? "" : undefined, variant_id: "", variant_name: "", specs: "" });
      setLastPrices((prev) => ({ ...prev, [key]: null }));
      return;
    }
    updateLine(key, {
      product_id: row.id,
      product_name: row.name,
      product_code: row.code,
      hsn_sac_code: row.hsn_sac_code || "",
      unit: row.unit,
      unit_price: String(Number(row.unit_price)),
      gst_rate: Number(row.gst_rate),
      variant_id: "",
      variant_name: "",
      specs: "",
    });
    loadVariants(row.id);
    fetchLastPrice(key, row.id);
  };

  const pickVariant = (key: string, productId: string, variantId: string) => {
    const v = (variantsByProduct[productId] || []).find((x) => x.id === variantId);
    updateLine(key, {
      variant_id: variantId,
      variant_name: v?.name || "",
      // A shade with its own price quotes at it; the rate can still be changed by hand
      ...(v && v.unit_price !== null && v.unit_price !== undefined ? { unit_price: String(Number(v.unit_price)) } : {}),
    });
    fetchLastPrice(key, productId, variantId || undefined);
  };

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        product_id: "",
        product_name: "",
        description: "",
        hsn_sac_code: "",
        unit: "NOS",
        quantity: "1",
        unit_price: "",
        discount_pct: "0",
        gst_rate: 18,
        variant_id: "",
        variant_name: "",
        specs: "",
        tolerance_pct: "",
        packing: "",
        delivery_date: "",
      },
    ]);
    setDirty(true);
  };

  const removeLine = (key: string) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
    setDirty(true);
  };

  // ---------- actions ----------
  const validateLines = () => {
    for (const [i, l] of lines.entries()) {
      if (!l.product_id) return `Line ${i + 1}: choose a product or a custom line`;
      if (l.product_id === CUSTOM && !l.product_name.trim()) return `Line ${i + 1}: enter a description`;
      if (!(Number(l.quantity) > 0)) return `Line ${i + 1}: quantity must be greater than zero`;
      if (l.unit_price === "" || Number(l.unit_price) < 0) return `Line ${i + 1}: enter a valid price`;
      const d = Number(l.discount_pct) || 0;
      if (d < 0 || d > 100) return `Line ${i + 1}: discount must be 0-100%`;
      if (l.tolerance_pct !== "" && !(Number(l.tolerance_pct) >= 0 && Number(l.tolerance_pct) <= 50)) return `Line ${i + 1}: tolerance must be 0-50%`;
    }
    if (header.valid_until && header.quote_date && header.valid_until < header.quote_date) {
      return "Valid until cannot be before the quote date";
    }
    return null;
  };

  const save = async (silent = false) => {
    if (!quote) return false;
    const problem = validateLines();
    if (problem) {
      error("Validation Error", problem);
      return false;
    }
    try {
      await api.put(`/api/Quotation/Update/${quote.id}`, {
        contact_id: header.contact_id || null,
        quote_date: header.quote_date,
        valid_until: header.valid_until,
        gst_type: header.gst_type,
        place_of_supply: header.place_of_supply || null,
        terms: header.terms,
        notes: header.notes,
        customer_notes: header.customer_notes,
        delivery_terms: header.delivery_terms,
        payment_terms: header.payment_terms,
        lines: lines.map((l) => ({
          product_id: l.product_id && l.product_id !== CUSTOM ? l.product_id : undefined,
          product_name: l.product_name.trim() || undefined,
          description: l.description.trim() || undefined,
          hsn_sac_code: l.hsn_sac_code.trim() || undefined,
          unit: l.unit || undefined,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          discount_pct: Number(l.discount_pct) || 0,
          gst_rate: l.gst_rate,
          variant_id: l.variant_id && l.product_id !== CUSTOM ? l.variant_id : undefined,
          tolerance_pct: l.tolerance_pct === "" ? null : Number(l.tolerance_pct),
          packing: l.packing.trim() || undefined,
          delivery_date: l.delivery_date || null,
        })),
      });
      if (!silent) success("Saved", "Draft quote saved");
      return true;
    } catch (err: any) {
      error("Save Failed", err.message || "Could not save the quote");
      return false;
    }
  };

  const run = async (fn: () => Promise<any>, okTitle: string, okMsg?: (res: any) => string) => {
    setBusy(true);
    try {
      const res = await fn();
      success(okTitle, okMsg ? okMsg(res) : res?.Message || "Done");
      await loadQuote();
      return res;
    } catch (err: any) {
      error("Action Failed", err.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    setBusy(true);
    if (await save()) await loadQuote();
    setBusy(false);
  };

  const handleSubmit = async () => {
    if (!quote) return;
    if (lines.length === 0) {
      error("No Lines", "Add at least one line before submitting");
      return;
    }
    setBusy(true);
    const saved = dirty ? await save(true) : true;
    setBusy(false);
    if (!saved) return;
    await run(() => api.patch(`/api/Quotation/Submit/${quote.id}`), "Quote Submitted", (res) => res?.Message);
  };

  const handleRevise = async () => {
    if (!quote) return;
    setBusy(true);
    try {
      const res: any = await api.post(`/api/Quotation/Revise/${quote.id}`);
      success("Revision Created", res?.Message || "New draft version created");
      navigate(`/quotes/${res?.AddtionalData}`);
    } catch (err: any) {
      error("Revise Failed", err.message || "Could not revise the quote");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!quote) return;
    setBusy(true);
    try {
      await api.delete(`/api/Quotation/Delete/${quote.id}`);
      success("Quote Deleted", `${quote.display_number} deleted`);
      navigate(quote.parent_quote_id ? `/quotes/${quote.parent_quote_id}` : "/quotes");
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete the quote");
    } finally {
      setBusy(false);
      setDeleteOpen(false);
    }
  };

  const submitReason = async () => {
    if (!quote || !reasonDialog) return;
    if (!reason.trim()) {
      error("Reason Required", "Please enter a reason");
      return;
    }
    const path = reasonDialog === "REJECT_APPROVAL" ? "Reject" : "MarkRejected";
    await run(() => api.patch(`/api/Quotation/${path}/${quote.id}`, { reason: reason.trim() }), "Updated");
    setReasonDialog(null);
    setReason("");
  };

  if (loading || !quote) {
    return <FormSkeleton />;
  }

  const status = quote.quote_status;
  const editDisabled = !canEdit || busy;

  return (
    <div className="space-y-4 pb-10">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="space-y-1">
          <Link to="/quotes" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="size-3.5" /> All quotes
          </Link>
          <div className="flex items-center gap-2.5 flex-wrap">
            <FileText className="size-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight font-mono">{quote.display_number}</h1>
            <QuoteStatusBadge status={status} expired={quote.is_expired} />
            {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
          </div>
          <p className="text-sm text-muted-foreground">
            {quote.account?.name} · Deal{" "}
            <Link to={`/deals/${quote.deal_id}`} className="text-primary hover:underline">
              {quote.deal?.name}
            </Link>
            {quote.owner && ` · Owner ${quote.owner.first_name} ${quote.owner.last_name || ""}`}
          </p>
        </div>

        {/* Status-dependent actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => window.open(`/api/Quotation/Pdf/${quote.id}`, "_blank")}
          >
            <Download className="size-4" />
            {status === "DRAFT" || status === "PENDING_APPROVAL" ? "Preview PDF" : "PDF"}
          </Button>

          {status === "DRAFT" && hasPermission("quote.update") && (
            <>
              <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={() => setDeleteOpen(true)} disabled={busy}>
                <Trash2 className="size-4" /> Delete
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleSave} disabled={busy || !dirty}>
                <Save className="size-4" /> Save
              </Button>
            </>
          )}
          {status === "DRAFT" && hasPermission("quote.submit") && (
            <Button size="sm" className="gap-1.5" onClick={handleSubmit} disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
              Submit
            </Button>
          )}

          {status === "PENDING_APPROVAL" && hasPermission("quote.approve") && !isMine && (
            <>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => (setReason(""), setReasonDialog("REJECT_APPROVAL"))} disabled={busy}>
                <XCircle className="size-4" /> Send Back
              </Button>
              <Button size="sm" className="gap-1.5" onClick={() => (setApproveComment(""), setApproveOpen(true))} disabled={busy}>
                <CheckCircle2 className="size-4" /> Approve
              </Button>
            </>
          )}

          {(status === "APPROVED" || status === "SENT") && !quote.is_expired && hasPermission("quote.send") && (
            <Button size="sm" className="gap-1.5" onClick={() => setSendOpen(true)} disabled={busy}>
              <Send className="size-4" /> {status === "SENT" ? "Resend" : "Send to Customer"}
            </Button>
          )}
          {(status === "APPROVED" || status === "SENT") && hasPermission("quote.update") && (
            <>
              {!quote.is_expired && (
                <Button variant="outline" size="sm" className="gap-1.5 text-emerald-700" onClick={() => setAcceptOpen(true)} disabled={busy}>
                  <ThumbsUp className="size-4" /> Accepted
                </Button>
              )}
              <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={() => (setReason(""), setReasonDialog("CUSTOMER_REJECT"))} disabled={busy}>
                <ThumbsDown className="size-4" /> Rejected
              </Button>
            </>
          )}
          {["PENDING_APPROVAL", "APPROVED", "SENT", "REJECTED"].includes(status) && hasPermission("quote.update") && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={handleRevise} disabled={busy}>
              <Copy className="size-4" /> Revise
            </Button>
          )}
        </div>
      </div>

      {/* Banners */}
      {status === "DRAFT" && quote.approval_comment && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm flex gap-2">
          <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <strong>Sent back by approver:</strong> {quote.approval_comment}
          </div>
        </div>
      )}
      {status === "PENDING_APPROVAL" && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="size-8 rounded-lg bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
                <ShieldCheck className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-foreground">
                  Approval Required
                </h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Currently assigned reviewer:{" "}
                  <strong className="text-foreground">
                    {quote.pending_approver
                      ? `${quote.pending_approver.first_name} ${quote.pending_approver.last_name || ""} (${quote.pending_approver.email || ""})`
                      : "Manager / Executive Reviewer"}
                  </strong>
                </p>
              </div>
            </div>
            {isMine && (
              <span className="text-[11px] font-semibold text-amber-800 dark:text-amber-300 bg-amber-500/20 px-2.5 py-1 rounded-full border border-amber-500/30">
                You cannot review your own quote
              </span>
            )}
          </div>

          {/* Triggered Reasons Pills */}
          <div className="flex flex-wrap gap-2 text-xs">
            {(!quote.approval_reasons || quote.approval_reasons.length === 0 || quote.approval_reasons.includes("DISCOUNT_ABOVE_LIMIT")) && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-background/80 border border-amber-500/30 text-amber-800 dark:text-amber-300 font-medium">
                <AlertCircle className="size-3.5 text-amber-600 shrink-0" />
                <span>Highest discount: <strong>{Number(quote.max_discount_pct)}%</strong> (exceeds submitter limit)</span>
              </div>
            )}
            {quote.approval_reasons?.includes("SELLING_BELOW_COST") && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-background/80 border border-red-500/30 text-red-700 dark:text-red-400 font-medium">
                <AlertCircle className="size-3.5 text-red-600 shrink-0" />
                <span>Line item(s) sell below product cost price</span>
              </div>
            )}
            {quote.approval_reasons?.includes("MARGIN_BELOW_FLOOR") && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-background/80 border border-amber-500/30 text-amber-800 dark:text-amber-300 font-medium">
                <AlertCircle className="size-3.5 text-amber-600 shrink-0" />
                <span>Line item(s) profit margin is below the minimum margin floor</span>
              </div>
            )}
          </div>
        </div>
      )}
      {quote.is_expired && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm flex gap-2">
          <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />
          This quote expired on {formatDate(quote.valid_until)}. Revise it to set a new validity date.
        </div>
      )}
      {status === "ACCEPTED" && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm flex gap-2">
          <CheckCircle2 className="size-4 text-emerald-600 shrink-0 mt-0.5" />
          Accepted on {formatDate(quote.accepted_at)}. The deal's products and amount were updated to match this quote.
        </div>
      )}
      {status === "REJECTED" && quote.customer_rejection_reason && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm">
          <strong>Customer rejected:</strong> {quote.customer_rejection_reason}
        </div>
      )}
      {status === "SUPERSEDED" && (
        <div className="rounded-xl border bg-muted/40 px-4 py-3 text-sm">
          This version was replaced by a newer revision. See versions below.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        {/* Main column */}
        <div className="xl:col-span-9 space-y-4">
          {/* Details */}
          <div className="bg-card border rounded-2xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1 col-span-2 md:col-span-1">
              <Label className="text-xs">Attention (contact)</Label>
              {canEdit ? (
                <SearchSelect
                  endpoint="/api/Contact/List"
                  extraParams={{ account_id: quote.account_id }}
                  value={header.contact_id}
                  selectedLabel={attentionLabel}
                  getLabel={(row) => `${row.first_name || ""} ${row.last_name || ""}`.trim() + (row.email ? ` (${row.email})` : "")}
                  placeholder="Search contacts"
                  allowEmpty
                  emptyLabel="-- None --"
                  onChange={(id, row) => {
                    setHeader({ ...header, contact_id: id });
                    setAttentionLabel(row ? `${row.first_name || ""} ${row.last_name || ""}`.trim() : "");
                    setDirty(true);
                  }}
                />
              ) : (
                <div className="text-sm h-9 flex items-center">
                  {quote.contact ? `${quote.contact.first_name} ${quote.contact.last_name || ""}` : "-"}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Quote date</Label>
              <Input
                type="date"
                value={header.quote_date}
                disabled={editDisabled}
                onChange={(e) => (setHeader({ ...header, quote_date: e.target.value }), setDirty(true))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Valid until</Label>
              <Input
                type="date"
                value={header.valid_until}
                disabled={editDisabled}
                onChange={(e) => (setHeader({ ...header, valid_until: e.target.value }), setDirty(true))}
              />
            </div>
            <div className="space-y-1 col-span-2 md:col-span-1">
              <Label className="text-xs">Place of supply</Label>
              <select
                value={header.place_of_supply}
                disabled={editDisabled}
                onChange={(e) => (setHeader({ ...header, place_of_supply: e.target.value }), setDirty(true))}
                className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm disabled:opacity-70"
              >
                <option value="">-- Not set --</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s.code} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">GST type</Label>
              <div className="flex gap-2">
                {(["INTRA", "INTER"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    disabled={editDisabled}
                    onClick={() => (setHeader({ ...header, gst_type: t }), setDirty(true))}
                    className={cn(
                      "flex-1 h-9 rounded-md border text-xs font-semibold transition-colors disabled:cursor-not-allowed",
                      header.gst_type === t ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"
                    )}
                  >
                    {t === "INTRA" ? "Same state (CGST + SGST)" : "Other state (IGST)"}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1 col-span-2 md:col-span-1">
              <Label className="text-xs">Delivery terms</Label>
              <select
                value={header.delivery_terms}
                disabled={editDisabled}
                onChange={(e) => (setHeader({ ...header, delivery_terms: e.target.value }), setDirty(true))}
                className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-sm disabled:opacity-70"
              >
                <option value="">-- Not set --</option>
                {DELIVERY_TERMS.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1 col-span-2 md:col-span-3">
              <Label className="text-xs">Payment terms</Label>
              <Input
                value={header.payment_terms}
                disabled={editDisabled}
                placeholder="e.g. 30% advance, balance against delivery"
                onChange={(e) => (setHeader({ ...header, payment_terms: e.target.value }), setDirty(true))}
              />
            </div>
            {quote.account && (
              <div className="col-span-2 text-xs text-muted-foreground self-end pb-2">
                Customer: {quote.account.state || "state not set"}
                {quote.account.gstin && ` · GSTIN ${quote.account.gstin}`}
              </div>
            )}
          </div>

          {/* Lines */}
          <div className="bg-card border rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-bold">Line items</h3>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={addLine} className="h-8 gap-1.5 text-xs" disabled={busy}>
                  <Plus className="size-3.5" /> Add line
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="text-left font-semibold px-3 py-2 min-w-[220px]">Item</th>
                    <th className="text-left font-semibold px-2 py-2 w-24">HSN/SAC</th>
                    <th className="text-right font-semibold px-2 py-2 w-20">Qty</th>
                    <th className="text-left font-semibold px-2 py-2 w-20">Unit</th>
                    <th className="text-right font-semibold px-2 py-2 w-28">Rate (₹)</th>
                    <th className="text-right font-semibold px-2 py-2 w-20">Disc %</th>
                    <th className="text-right font-semibold px-2 py-2 w-20">GST</th>
                    <th className="text-right font-semibold px-3 py-2 w-28">Amount</th>
                    {canEdit && <th className="w-10" />}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const c = computeLine(l);
                    const isCustom = l.product_id === CUSTOM;
                    const overLimit = myLimit !== null && (Number(l.discount_pct) || 0) > myLimit;
                    const isFlagged =
                      Boolean(quote?.flagged_line_ids?.includes(l.key)) ||
                      Boolean(quote?.flagged_line_ids?.includes(`line-${idx + 1}`)) ||
                      Boolean(l.product_id && quote?.flagged_line_ids?.includes(l.product_id));
                    return (
                      <tr key={l.key} className={cn("border-t align-top transition-colors", isFlagged && "bg-amber-500/5")}>
                        <td className="px-3 py-2 space-y-1">
                          {canEdit ? (
                            <>
                              {hasPermission("product.read") ? (
                                <SearchSelect
                                  endpoint="/api/Product/List"
                                  extraParams={{ active: "true" }}
                                  value={l.product_id}
                                  selectedLabel={l.product_id === CUSTOM ? l.product_name || "+ Custom line" : l.product_name}
                                  getLabel={(row) => `${row.name} (${row.code})`}
                                  placeholder="Search products"
                                  allowEmpty
                                  emptyLabel="-- Select product --"
                                  extraOptions={[{ value: CUSTOM, label: "+ Custom line" }]}
                                  onChange={(id, row) => pickProduct(l.key, id, row)}
                                  className="text-xs"
                                />
                              ) : (
                                <select
                                  value={l.product_id === CUSTOM ? CUSTOM : ""}
                                  onChange={(e) => pickProduct(l.key, e.target.value)}
                                  className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs"
                                >
                                  <option value="">-- Select product --</option>
                                  <option value={CUSTOM}>+ Custom line</option>
                                </select>
                              )}
                              {(isCustom || l.product_id) && (
                                <Input
                                  value={l.product_name}
                                  placeholder={isCustom ? "Description *" : "Line name"}
                                  onChange={(e) => updateLine(l.key, { product_name: e.target.value })}
                                  className="h-8 text-xs"
                                />
                              )}
                              <Input
                                value={l.description}
                                placeholder="Details (optional)"
                                onChange={(e) => updateLine(l.key, { description: e.target.value })}
                                className="h-8 text-xs"
                              />
                              {!isCustom && l.product_id && (variantsByProduct[l.product_id]?.length || l.variant_id) ? (
                                <select
                                  value={l.variant_id}
                                  onChange={(e) => pickVariant(l.key, l.product_id, e.target.value)}
                                  className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs"
                                >
                                  <option value="">-- Shade / variant --</option>
                                  {l.variant_id && !(variantsByProduct[l.product_id] || []).some((v) => v.id === l.variant_id) && (
                                    <option value={l.variant_id}>{l.variant_name || "(inactive shade)"}</option>
                                  )}
                                  {(variantsByProduct[l.product_id] || []).map((v) => (
                                    <option key={v.id} value={v.id}>
                                      {v.name} ({v.code})
                                    </option>
                                  ))}
                                </select>
                              ) : null}
                              {l.specs && <div className="text-[11px] text-muted-foreground">{l.specs}</div>}
                              {lastPrices[l.key] && (
                                <div className="flex items-center gap-2 text-[11px] rounded-md bg-sky-500/10 text-sky-800 dark:text-sky-300 px-2 py-1">
                                  <span>
                                    Last quoted to this buyer: <strong>{formatINR(lastPrices[l.key]!.unit_price)}</strong> / {lastPrices[l.key]!.unit}
                                    {lastPrices[l.key]!.variant_name ? ` (${lastPrices[l.key]!.variant_name})` : ""} on {formatDate(lastPrices[l.key]!.quote_date)} · {lastPrices[l.key]!.quote_number}
                                  </span>
                                  <button
                                    type="button"
                                    className="ml-auto font-semibold underline shrink-0"
                                    onClick={() => updateLine(l.key, { unit_price: String(Number(lastPrices[l.key]!.unit_price)) })}
                                  >
                                    Use
                                  </button>
                                </div>
                              )}
                              <div className="grid grid-cols-3 gap-1.5">
                                <Input
                                  type="number"
                                  min={0}
                                  max={50}
                                  step="0.5"
                                  value={l.tolerance_pct}
                                  placeholder="Tol. ±%"
                                  title="Quantity tolerance, plus or minus this percent"
                                  onChange={(e) => updateLine(l.key, { tolerance_pct: e.target.value })}
                                  className="h-8 text-xs"
                                />
                                <Input
                                  value={l.packing}
                                  placeholder="Packing"
                                  title="e.g. Rolls of 100 m"
                                  onChange={(e) => updateLine(l.key, { packing: e.target.value })}
                                  className="h-8 text-xs"
                                />
                                <Input
                                  type="date"
                                  value={l.delivery_date}
                                  title="Delivery by"
                                  onChange={(e) => updateLine(l.key, { delivery_date: e.target.value })}
                                  className="h-8 text-xs"
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="font-medium text-sm">{l.product_name}</div>
                              {l.product_code && <code className="text-[10px] text-muted-foreground">{l.product_code}</code>}
                              {l.variant_name && <div className="text-xs">Shade: {l.variant_name}</div>}
                              {l.specs && <div className="text-[11px] text-muted-foreground">{l.specs}</div>}
                              {l.description && <div className="text-muted-foreground">{l.description}</div>}
                              {(l.tolerance_pct || l.packing || l.delivery_date) && (
                                <div className="text-[11px] text-muted-foreground">
                                  {[
                                    l.tolerance_pct ? `Tolerance ±${l.tolerance_pct}%` : null,
                                    l.packing ? `Packing: ${l.packing}` : null,
                                    l.delivery_date ? `Delivery by ${formatDate(l.delivery_date)}` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </div>
                              )}
                            </>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {canEdit ? (
                            <Input value={l.hsn_sac_code} onChange={(e) => updateLine(l.key, { hsn_sac_code: e.target.value })} className="h-8 text-xs font-mono" />
                          ) : (
                            <span className="font-mono">{l.hsn_sac_code || "-"}</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {canEdit ? (
                            <Input type="number" min={0.01} step="0.01" value={l.quantity} onChange={(e) => updateLine(l.key, { quantity: e.target.value })} className="h-8 text-xs text-right" />
                          ) : (
                            Number(l.quantity)
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {canEdit ? (
                            <Input value={l.unit} onChange={(e) => updateLine(l.key, { unit: e.target.value.toUpperCase() })} className="h-8 text-xs uppercase" />
                          ) : (
                            l.unit
                          )}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {canEdit ? (
                            <Input type="number" min={0} step="0.01" value={l.unit_price} onChange={(e) => updateLine(l.key, { unit_price: e.target.value })} className="h-8 text-xs text-right" />
                          ) : (
                            formatINR(l.unit_price)
                          )}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {canEdit ? (
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              step="0.01"
                              value={l.discount_pct}
                              onChange={(e) => updateLine(l.key, { discount_pct: e.target.value })}
                              className={cn("h-8 text-xs text-right", overLimit && "border-amber-500 text-amber-700")}
                              title={overLimit ? `Above your ${myLimit}% limit: needs approval` : undefined}
                            />
                          ) : Number(l.discount_pct) ? (
                            `${Number(l.discount_pct)}%`
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {canEdit ? (
                            <select
                              value={l.gst_rate}
                              onChange={(e) => updateLine(l.key, { gst_rate: Number(e.target.value) })}
                              className="h-8 w-full rounded-md border border-border bg-background px-1 text-xs"
                            >
                              {GST_RATES.map((r) => (
                                <option key={r} value={r}>
                                  {r}%
                                </option>
                              ))}
                            </select>
                          ) : (
                            `${l.gst_rate}%`
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="font-semibold">{formatINR(c.total)}</div>
                          <div className="text-[10px] text-muted-foreground">Taxable {formatINR(c.taxable)}</div>
                        </td>
                        {canEdit && (
                          <td className="px-1 py-2">
                            <Button variant="ghost" size="icon" className="size-7" onClick={() => removeLine(l.key)} title="Remove line">
                              <Trash2 className="size-3.5 text-red-500" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {lines.length === 0 && (
                    <tr>
                      <td colSpan={canEdit ? 9 : 8} className="text-center py-8 text-muted-foreground">
                        No lines yet.{canEdit && " Add products or custom lines."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="border-t px-4 py-3 flex flex-col md:flex-row justify-between gap-4">
              <div className="text-xs space-y-1 text-muted-foreground">
                <div>
                  Highest discount: <strong className="text-foreground">{totals.maxDiscount}%</strong>
                  {myLimit !== null && ` · your limit ${myLimit}%`}
                </div>
                {canEdit && needsApproval && (
                  <div className="text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="size-3.5" /> This quote will need manager approval when submitted.
                  </div>
                )}
              </div>
              <div className="text-sm space-y-1 min-w-[260px]">
                <Row label="Subtotal" value={formatINR(totals.subtotal)} />
                {totals.discount > 0 && <Row label="Discount" value={`- ${formatINR(totals.discount)}`} />}
                <Row label="Taxable amount" value={formatINR(totals.taxable)} />
                {header.gst_type === "INTRA" || (!canEdit && quote.gst_type === "INTRA") ? (
                  <>
                    <Row label="CGST" value={formatINR(totals.cgst)} />
                    <Row label="SGST" value={formatINR(totals.sgst)} />
                  </>
                ) : (
                  <Row label="IGST" value={formatINR(totals.igst)} />
                )}
                {totals.roundOff !== 0 && <Row label="Round off" value={formatINR(totals.roundOff)} />}
                <div className="flex justify-between border-t pt-1.5 text-base font-bold">
                  <span>Grand total</span>
                  <span>{formatINR(totals.grand)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="bg-card border rounded-2xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs sm:text-sm font-semibold">Terms & conditions (printed)</Label>
              <Textarea
                rows={4}
                value={header.terms}
                disabled={editDisabled}
                onChange={(e) => (setHeader({ ...header, terms: e.target.value }), setDirty(true))}
                className="text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Notes for customer (printed)</Label>
              <Textarea
                rows={3}
                value={header.customer_notes}
                disabled={editDisabled}
                onChange={(e) => (setHeader({ ...header, customer_notes: e.target.value }), setDirty(true))}
                className="text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Internal notes (not printed)</Label>
              <Textarea
                rows={3}
                value={header.notes}
                disabled={editDisabled}
                onChange={(e) => (setHeader({ ...header, notes: e.target.value }), setDirty(true))}
                className="text-sm"
              />
            </div>
          </div>
        </div>

        {/* Side column */}
        <div className="xl:col-span-3 space-y-4">
          {(quote.versions?.length || 0) > 1 && (
            <div className="bg-card border rounded-2xl p-4 space-y-2">
              <h3 className="text-sm font-bold">Versions</h3>
              {quote.versions!.map((v) => (
                <Link
                  key={v.id}
                  to={`/quotes/${v.id}`}
                  className={cn(
                    "flex items-center justify-between rounded-lg px-2.5 py-1.5 text-xs hover:bg-muted",
                    v.id === quote.id && "bg-muted font-semibold"
                  )}
                >
                  <span className="font-mono">{v.display_number}</span>
                  <span className="text-muted-foreground">{QUOTE_STATUS_LABELS[v.quote_status]}</span>
                </Link>
              ))}
            </div>
          )}

          <div className="bg-card border rounded-2xl p-4 space-y-3">
            <h3 className="text-sm font-bold flex items-center gap-1.5">
              <History className="size-4" /> History
            </h3>
            <ol className="space-y-3">
              {(quote.history || []).map((h) => (
                <li key={h.id} className="text-xs border-l-2 border-primary/30 pl-3">
                  <div className="font-semibold text-foreground">{HISTORY_LABELS[h.action] || h.action}</div>
                  {h.comment && <div className="text-muted-foreground">{h.comment}</div>}
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {h.created_by ? `${h.created_by.first_name} ${h.created_by.last_name || ""} · ` : ""}
                    {formatDateTime(h.created_on)}
                  </div>
                </li>
              ))}
            </ol>
            {quote.approved_by && quote.approved_at && (
              <div className="text-xs text-muted-foreground border-t pt-2">
                Approved by {quote.approved_by.first_name} {quote.approved_by.last_name || ""} on {formatDate(quote.approved_at)}
              </div>
            )}
            {quote.sent_at && (
              <div className="text-xs text-muted-foreground">
                Last sent to {quote.sent_to_email} on {formatDate(quote.sent_at)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send */}
      <SendQuoteDialog quote={quote} open={sendOpen} onOpenChange={setSendOpen} onSent={loadQuote} />

      {/* Approve */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Approve {quote.display_number}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Highest discount: <strong className="text-foreground">{Number(quote.max_discount_pct)}%</strong> · Grand total: <strong className="text-foreground">{formatINR(quote.grand_total)}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 text-xs sm:text-sm">
            {quote.approval_reasons && quote.approval_reasons.length > 0 && (
              <div className="p-3 sm:p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-300 space-y-1.5">
                <span className="font-semibold flex items-center gap-1.5 text-xs sm:text-sm">
                  <AlertCircle className="size-4 text-amber-600" />
                  Approval Trigger Reasons:
                </span>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  {quote.approval_reasons.includes("DISCOUNT_ABOVE_LIMIT") && (
                    <li>Requested discount ({Number(quote.max_discount_pct)}%) is above the submitter's limit.</li>
                  )}
                  {quote.approval_reasons.includes("SELLING_BELOW_COST") && (
                    <li>One or more line items sell below product cost price.</li>
                  )}
                  {quote.approval_reasons.includes("MARGIN_BELOW_FLOOR") && (
                    <li>One or more line items have margins below the 10% policy floor.</li>
                  )}
                </ul>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Approval Comment (optional)</Label>
              <Textarea
                rows={3}
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                placeholder="e.g. Approved per regional executive agreement"
              />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button
              disabled={busy}
              onClick={async () => {
                await run(() => api.patch(`/api/Quotation/Approve/${quote.id}`, { comment: approveComment.trim() || undefined }), "Quote Approved");
                setApproveOpen(false);
              }}
            >
              {busy ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reason (send back / customer rejected) */}
      <Dialog open={!!reasonDialog} onOpenChange={(open) => !open && setReasonDialog(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">{reasonDialog === "REJECT_APPROVAL" ? "Send back to draft" : "Customer rejected the quote"}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {reasonDialog === "REJECT_APPROVAL"
                ? "Tell the owner what to change. The quote becomes editable again."
                : "Record why the customer declined. You can still revise the quote later."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label className="text-xs sm:text-sm font-semibold">Reason *</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setReasonDialog(null)}>Cancel</Button>
            <Button variant="destructive" disabled={busy || !reason.trim()} onClick={submitReason}>
              {busy ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Mark {quote.display_number} as accepted?</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              The deal's product lines will be replaced with this quote's lines and the deal amount set to{" "}
              <strong className="text-foreground">{formatINR(quote.taxable_total)}</strong> (before GST). Only one quote per
              deal can be accepted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setAcceptOpen(false)}>Cancel</Button>
            <Button
              disabled={busy}
              onClick={async () => {
                await run(() => api.patch(`/api/Quotation/MarkAccepted/${quote.id}`), "Quote Accepted");
                setAcceptOpen(false);
              }}
            >
              {busy ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Mark Accepted
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg text-red-600">Delete {quote.display_number}?</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {quote.parent_quote_id
                ? "This discards the revision and restores the previous version."
                : "This draft quote will be permanently deleted."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={busy}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
